import { useState, useEffect } from 'react';
import { Plus, Trash2, ExternalLink, ToggleLeft, ToggleRight, Search, Edit2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LINK_CATEGORIES, getAllPlatforms, formatSocialUrl } from '@/data/platforms';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getPlatformInputLabel = (platform) => {
  if (platform.smartInput === 'phone') return 'Phone Number';
  if (platform.smartInput === 'email') return 'Email Address';
  if (platform.smartInput === 'invite') return 'Community Invite Link or Code';
  return 'Profile URL or Username';
};

const SocialLinksEditor = () => {
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('social');
  const [editingId, setEditingId] = useState(null);
  const [editUrl, setEditUrl] = useState('');

  useEffect(() => { fetchSocialLinks(); }, []);

  const fetchSocialLinks = async () => {
    try {
      const res = await axios.get(`${API}/social-links`);
      setSocialLinks(res.data);
    } catch (err) {
      toast.error('Failed to load social links');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedPlatform || !linkUrl) return;
    const formattedUrl = formatSocialUrl(selectedPlatform.id, linkUrl);
    try {
      const res = await axios.post(`${API}/social-links`, { platform: selectedPlatform.id, url: formattedUrl });
      setSocialLinks([...socialLinks, res.data]);
      toast.success(`${selectedPlatform.name} added`);
      resetDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const resetDialog = () => {
    setIsAddOpen(false);
    setSelectedPlatform(null);
    setLinkUrl('');
    setSearchQuery('');
    setSelectedCategory('social');
  };

  const handleToggle = async (link) => {
    try {
      const res = await axios.put(`${API}/social-links/${link.platform}`, { is_active: !link.is_active });
      setSocialLinks(socialLinks.map(l => l.id === link.id ? res.data : l));
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (link) => {
    try {
      await axios.delete(`${API}/social-links/${link.platform}`);
      setSocialLinks(socialLinks.filter(l => l.id !== link.id));
      toast.success('Icon removed');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const startEdit = (link) => { setEditingId(link.platform); setEditUrl(link.url); };

  const handleEditSave = async (link) => {
    if (!editUrl.trim()) { toast.error('URL is required'); return; }
    const formattedUrl = formatSocialUrl(link.platform, editUrl);
    try {
      const res = await axios.put(`${API}/social-links/${link.platform}`, { url: formattedUrl });
      setSocialLinks(socialLinks.map(l => l.id === link.id ? res.data : l));
      setEditingId(null);
      toast.success('Link updated');
    } catch (err) { toast.error('Failed to update'); }
  };

  const existingPlatforms = socialLinks.map(l => l.platform);
  const allPlatforms = getAllPlatforms();

  const filteredPlatforms = allPlatforms.filter(p =>
    (!selectedCategory || p.category === selectedCategory) &&
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    !existingPlatforms.includes(p.id)
  );

  if (loading) {
    return (
      <div className="max-w-2xl flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Social Icons</h1>
          <p className="text-[#A1A1AA] mt-1">Manage the social media icons shown on your profile</p>
        </div>
        <Button data-testid="add-social-icon-btn" onClick={() => setIsAddOpen(true)}
          className="rounded-full bg-violet-500 hover:bg-violet-600 text-white gap-2">
          <Plus className="w-4 h-4" /> Add Icon
        </Button>
      </div>

      {socialLinks.length === 0 ? (
        <div className="text-center py-16">
          <ExternalLink className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
          <h3 className="font-outfit font-semibold text-lg text-white mb-2">No social icons yet</h3>
          <p className="text-[#A1A1AA] mb-6">Add social media icons to display on your profile</p>
          <Button data-testid="add-first-social-btn" onClick={() => setIsAddOpen(true)}
            className="rounded-full bg-violet-500 hover:bg-violet-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add your first icon
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {socialLinks.map((link) => {
            const platformInfo = allPlatforms.find(p => p.id === link.platform);
            return (
              <div key={link.id} data-testid={`social-link-${link.platform}`}
                className={`rounded-2xl bg-[#121212] border border-[#27272A] p-4 transition-all ${!link.is_active ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/[0.06] flex items-center justify-center text-lg">
                    {platformInfo?.icon || link.platform.slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white">{platformInfo?.name || link.platform}</h3>
                    {editingId === link.platform ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input data-testid={`edit-url-${link.platform}`} value={editUrl} onChange={e => setEditUrl(e.target.value)}
                          className="flex-1 bg-[#1E1E1E] border border-[#27272A] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-violet-500"
                          placeholder={platformInfo?.placeholder || 'https://...'} autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') handleEditSave(link); if (e.key === 'Escape') setEditingId(null); }} />
                        <button onClick={() => handleEditSave(link)} className="p-1.5 rounded-lg hover:bg-green-500/10">
                          <Check className="w-4 h-4 text-green-400" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="p-1.5 rounded-lg hover:bg-white/5">
                          <X className="w-4 h-4 text-[#71717A]" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-[#71717A] truncate">{link.url}</p>
                    )}
                  </div>
                  {editingId !== link.platform && (
                  <div className="flex items-center gap-2">
                    <button data-testid={`edit-social-${link.platform}`} onClick={() => startEdit(link)} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
                      <Edit2 className="w-4 h-4 text-[#A1A1AA]" />
                    </button>
                    <button data-testid={`toggle-social-${link.platform}`} onClick={() => handleToggle(link)} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
                      {link.is_active ? <ToggleRight className="w-5 h-5 text-violet-400" /> : <ToggleLeft className="w-5 h-5 text-[#71717A]" />}
                    </button>
                    <button data-testid={`delete-social-${link.platform}`} onClick={() => handleDelete(link)} className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={isAddOpen} onOpenChange={(v) => { if (!v) resetDialog(); else setIsAddOpen(true); }}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Add Social Icon</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Choose a platform to display as an icon on your profile</DialogDescription>
          </DialogHeader>

          {selectedPlatform ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-[#1E1E1E]">
                <span className="text-3xl">{selectedPlatform.icon}</span>
                <div>
                  <h3 className="font-semibold text-white">{selectedPlatform.name}</h3>
                  <p className="text-sm text-[#71717A]">{selectedPlatform.categoryName}</p>
                </div>
              </div>
              <div>
                <Label className="text-[#EDEDED]">{getPlatformInputLabel(selectedPlatform)}</Label>
                <Input data-testid="social-url-input" value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white"
                  placeholder={selectedPlatform.placeholder} autoFocus />
                {selectedPlatform.hint && (
                  <p className="text-xs text-violet-400/70 mt-1.5">{selectedPlatform.hint}</p>
                )}
                
              </div>
              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setSelectedPlatform(null)}
                  className="bg-transparent border-[#27272A] text-white hover:bg-white/5">Back</Button>
                <Button data-testid="save-social-btn" onClick={handleAdd} disabled={!linkUrl}
                  className="bg-violet-500 hover:bg-violet-600 text-white">Add Icon</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                <Input data-testid="platform-search" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#1E1E1E] border-[#27272A] text-white"
                  placeholder="Search platforms..." />
              </div>

              <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="flex-1 overflow-hidden flex flex-col">
                <TabsList className="bg-[#1E1E1E] border border-[#27272A] flex-wrap h-auto gap-1 p-1">
                  {Object.entries(LINK_CATEGORIES).map(([id, cat]) => (
                    <TabsTrigger key={id} value={id} className="data-[state=active]:bg-violet-500/20 text-xs px-2 py-1.5">
                      {cat.name}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <div className="flex-1 overflow-y-auto mt-4">
                  <div className="grid grid-cols-2 gap-2">
                    {filteredPlatforms.map((platform) => (
                      <button key={platform.id} data-testid={`select-${platform.id}`}
                        onClick={() => setSelectedPlatform(platform)}
                        className="p-3 rounded-xl border bg-[#1E1E1E] border-[#27272A] hover:border-violet-500/50 text-left transition-all">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{platform.icon}</span>
                          <span className="font-medium text-white text-sm">{platform.name}</span>
                        </div>
                      </button>
                    ))}
                    {filteredPlatforms.length === 0 && (
                      <p className="col-span-2 text-center text-[#71717A] py-8 text-sm">No platforms found</p>
                    )}
                  </div>
                </div>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialLinksEditor;
